import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { checkRegistrationAttempts, recordRegistrationAttempt } from '../services/userService'
import './AuthPages.css'

function RegisterPage() {
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [confirmPassword, setConfirmPassword] = useState('')
    const [showPassword, setShowPassword] = useState(false)
    const [retentionAccepted, setRetentionAccepted] = useState(false)
    const [showRetentionModal, setShowRetentionModal] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [localError, setLocalError] = useState(null)
    const { register, error, setError, user } = useAuth()
    const navigate = useNavigate()

    useEffect(() => {
        if (user) {
            navigate('/identify', { replace: true })
        }
    }, [user, navigate])

    const performRegistration = async (emailToUse, passwordToUse, accepted) => {
        setIsLoading(true)
        setLocalError(null)
        setError(null)
        console.log('Starting registration process for:', emailToUse)

        try {
            // Check for registration blocking (Non-blocking catch)
            try {
                console.log('Checking registration attempts...')
                const isBlocked = await checkRegistrationAttempts(emailToUse)
                if (isBlocked) {
                    console.warn('Registration blocked due to too many attempts')
                    setLocalError('Too many registration attempts. Please try again in 24 hours.')
                    setIsLoading(false)
                    return
                }

                // Record the attempt
                console.log('Recording registration attempt...')
                await recordRegistrationAttempt(emailToUse)
            } catch (fsErr) {
                console.error('Firestore check failed (continuing to Auth):', fsErr)
            }

            // Attempt registration
            console.log('Calling auth register...')
            await register(emailToUse, passwordToUse, accepted)
            console.log('Registration successful, navigating...')
            navigate('/identify')
        } catch (err) {
            console.error('Registration failed:', err)
            // Error is primarily handled by AuthContext, but let's show it here too if possible
            if (!error && (err.message || err.code)) {
                setLocalError(err.message || 'Registration failed. Please check if your Firebase Auth / Firestore is enabled.')
            }
        } finally {
            setIsLoading(false)
        }
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        setLocalError(null)
        setError(null)

        // Validate passwords match
        if (password !== confirmPassword) {
            setLocalError('Passwords do not match.')
            return
        }

        // Validate password strength
        if (password.length < 6) {
            setLocalError('Password must be at least 6 characters.')
            return
        }

        // Check retention policy
        if (!retentionAccepted) {
            console.log('Retention policy not yet accepted, showing modal')
            setShowRetentionModal(true)
            return
        }

        await performRegistration(email, password, true)
    }

    const handleAcceptRetention = async () => {
        console.log('Retention policy accepted in modal')
        setRetentionAccepted(true)
        setShowRetentionModal(false)
        // Automatically proceed with registration using current state
        await performRegistration(email, password, true)
    }

    const displayError = localError || error

    return (
        <div className="auth-page">
            <div className="auth-background">
                <div className="auth-bg-pattern" />
            </div>

            <div className="auth-container">
                <div className="auth-card card-glass fade-in">
                    {/* Logo */}
                    <div className="auth-logo">
                        <span className="auth-logo-icon">☕</span>
                        <h1 className="auth-logo-text">WantedCup</h1>
                        <p className="auth-logo-tagline">Create Your Account</p>
                    </div>

                    {/* Form */}
                    <form onSubmit={handleSubmit} className="auth-form">
                        {displayError && (
                            <div className="auth-error">
                                <span className="auth-error-icon">⚠</span>
                                {displayError}
                            </div>
                        )}

                        <div className="form-group">
                            <label htmlFor="email" className="form-label">Email Address</label>
                            <div className="input-wrapper">
                                <span className="input-icon">✉</span>
                                <input
                                    id="email"
                                    type="email"
                                    className="form-input with-icon"
                                    placeholder="example@email.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    autoComplete="email"
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            <label htmlFor="password" className="form-label">Password</label>
                            <div className="input-wrapper">
                                <span className="input-icon">🔒</span>
                                <input
                                    id="password"
                                    type={showPassword ? 'text' : 'password'}
                                    className="form-input with-icon"
                                    placeholder="••••••••"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    autoComplete="new-password"
                                />
                                <button
                                    type="button"
                                    className="password-toggle"
                                    onClick={() => setShowPassword(!showPassword)}
                                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                                >
                                    {showPassword ? '👁' : '👁‍🗨'}
                                </button>
                            </div>
                        </div>

                        <div className="form-group">
                            <label htmlFor="confirmPassword" className="form-label">Confirm Password</label>
                            <div className="input-wrapper">
                                <span className="input-icon">🔒</span>
                                <input
                                    id="confirmPassword"
                                    type={showPassword ? 'text' : 'password'}
                                    className="form-input with-icon"
                                    placeholder="••••••••"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    required
                                    autoComplete="new-password"
                                />
                            </div>
                        </div>

                        <div className="form-group checkbox-group">
                            <label className="checkbox-label">
                                <input
                                    type="checkbox"
                                    checked={retentionAccepted}
                                    onChange={(e) => setRetentionAccepted(e.target.checked)}
                                    className="checkbox-input"
                                />
                                <span className="checkbox-custom" />
                                <span className="checkbox-text">
                                    I agree to the{' '}
                                    <button
                                        type="button"
                                        className="inline-link"
                                        onClick={() => setShowRetentionModal(true)}
                                    >
                                        Data Retention Policy
                                    </button>
                                </span>
                            </label>
                        </div>

                        <button
                            type="submit"
                            className="btn btn-primary auth-submit"
                            disabled={isLoading}
                        >
                            {isLoading ? 'Creating Account...' : 'Register Now'}
                        </button>
                    </form>

                    {/* Footer */}
                    <div className="auth-footer">
                        <p>
                            Already have an account?{' '}
                            <Link to="/login" className="auth-link">Sign In</Link>
                        </p>
                    </div>
                </div>
            </div>

            {/* Retention Policy Modal */}
            {showRetentionModal && (
                <div className="modal-overlay" onClick={() => setShowRetentionModal(false)}>
                    <div className="modal-content card-glass" onClick={(e) => e.stopPropagation()}>
                        <button
                            className="modal-close"
                            onClick={() => setShowRetentionModal(false)}
                            aria-label="Close modal"
                        >
                            ✕
                        </button>

                        <h2 className="modal-title text-gold">Data Retention Policy</h2>

                        <div className="modal-body">
                            <p>
                                At WantedCup, we are committed to protecting your privacy. By registering,
                                you agree to our data retention practices:
                            </p>

                            <ul className="policy-list">
                                <li>
                                    We retain your personal data only for as long as necessary to provide
                                    our vintage tableware identification services and comply with legal obligations.
                                </li>
                                <li>
                                    Your uploaded images and search history are stored securely to improve our
                                    identification algorithms.
                                </li>
                                <li>
                                    You can request the deletion of your data at any time via your account settings.
                                </li>
                                <li>
                                    For detailed information, please review our full{' '}
                                    <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>{' '}
                                    and{' '}
                                    <a href="/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>.
                                </li>
                            </ul>

                            <p className="policy-note">
                                This policy is effective immediately upon acceptance.
                            </p>
                        </div>

                        <button
                            className="btn btn-primary modal-accept"
                            onClick={handleAcceptRetention}
                        >
                            I Understand and Accept
                        </button>
                    </div>
                </div>
            )}
        </div>
    )
}

export default RegisterPage
